import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import useSWRMutation from 'swr/mutation';

import { Container, Flexbox, Label } from '@seuk/design-system/atoms';
import { Button, ControlledSelect, Radio, TextField } from '@seuk/design-system/molecules';
import { showToastMessage, SmallMessagesToaster } from '@seuk/design-system/organisms';

import { Main as MainContainer } from '../../components';
import { ContactMethod } from '../../components/contact-method/contact-method';
import { ContactOptions } from '../../components/contact-options/contact-options';
import { Form, FormPageTitle, FormParagraph, FormSection, sendRequest } from '../../components/forms/forms';
import type { QuoteFormValues } from '../../components/forms/types';
import { IndustrySector } from '../../components/industry-sector/industry-sector';
import { Privacy } from '../../components/privacy/privacy';

const RadioGroup = styled(Flexbox)`
  gap: ${({ theme }) => theme.spacing(2)};
  flex-direction: column;
`;

const ENERGY_SPEND_OPTIONS = ['£0 - £50,000', '£50,000 - £1m', '£1m - £5m', '£5m - £10m', '£10m+'] as const;
const YES_NO = ['Yes', 'No'] as const;
const YES_NO_MAYBE = ['Yes', 'No', 'Not Sure'] as const;
const FIND_US_OPTIONS = [
  'Please select...',
  'Digital advert',
  'Email',
  'Event',
  'Print',
  'Search engine e.g. Google',
  'Shell Energy team',
  'Social media e.g. LinkedIn',
  'Website – please specify',
  'Word of mouth',
  'Other – please specify',
];
const FIND_US_OTHER = 'Other – please specify';

const DEFAULT_BUSINESS_TYPE = 'medium';

interface FormInnerProps {
  onSubmit: () => void;
}
const FormInner = ({ onSubmit }: FormInnerProps) => {
  const router = useRouter();
  const methods = useForm<QuoteFormValues>({
    mode: 'onChange',
    defaultValues: {
      timeToCall: 'anytime',
    },
  });

  const thirdParty = methods.watch('thirdParty');
  const findUs = methods.watch('findUs');

  const { trigger: submitForm, isMutating } = useSWRMutation('/api/quote', sendRequest<QuoteFormValues>);

  const onSubmitLocal = async (payload: QuoteFormValues) => {
    const businessType = router.query.businessType;

    try {
      await submitForm({
        ...payload,
        businessType: businessType && !Array.isArray(businessType) ? businessType : DEFAULT_BUSINESS_TYPE,
      });
      onSubmit();
    } catch (e) {
      showToastMessage({
        variant: 'small',
        type: 'danger',
        textMessage: 'Sorry, there has been a problem, please try again later',
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <Form as="form" onSubmit={methods.handleSubmit(onSubmitLocal)}>
        <TextField
          id="firstName"
          label="First name*"
          aria-required="true"
          {...methods.register('firstName', {
            required: true,
          })}
        />
        <TextField
          id="lastName"
          label="Surname*"
          aria-required="true"
          {...methods.register('lastName', {
            required: true,
          })}
        />
        <TextField
          id="company"
          label="Company*"
          aria-required="true"
          {...methods.register('company', { required: true })}
        />
        <TextField
          id="jobTitle"
          label="Job title*"
          aria-required="true"
          {...methods.register('jobTitle', { required: true })}
        />
        <ContactMethod methods={methods} />

        <RadioGroup>
          <Label>Total annual energy spend*</Label>
          {ENERGY_SPEND_OPTIONS.map((option) => {
            return (
              <Radio
                name="totalSpend"
                key={option}
                label={option}
                value={option}
                {...methods.register('totalSpend', { required: true })}
              />
            );
          })}
        </RadioGroup>
        <TextField
          id="annualConsumption"
          label="Estimated annual energy consumption (kWh)"
          {...methods.register('annualConsumption')}
        />
        <IndustrySector methods={methods} />

        <RadioGroup>
          <Label>Do you purchase energy via a third party?*</Label>
          {YES_NO.map((option) => {
            return (
              <Radio
                name="thirdParty"
                key={option}
                label={option}
                value={option}
                {...methods.register('thirdParty', { required: true })}
              />
            );
          })}
        </RadioGroup>
        {thirdParty === 'Yes' && (
          <TextField
            id="thirdPartyNameLabel"
            label="Name of third party"
            {...methods.register('thirdPartyNameLabel')}
          />
        )}
        <RadioGroup>
          <Label>Are you an existing customer?*</Label>
          {YES_NO.map((option) => {
            return (
              <Radio
                name="existingCustomer"
                key={option}
                label={option}
                value={option}
                {...methods.register('existingCustomer', { required: true })}
              />
            );
          })}
        </RadioGroup>
        <RadioGroup>
          <Label>Are you classified as a Microbusiness customer?*</Label>
          {YES_NO_MAYBE.map((option) => {
            return (
              <Radio
                name="microbusinessCustomer"
                key={option}
                label={option}
                value={option}
                {...methods.register('microbusinessCustomer', { required: true })}
              />
            );
          })}
        </RadioGroup>
        <ControlledSelect
          label="How did you find us?"
          name="findUs"
          id="findUs"
          items={FIND_US_OPTIONS.map((sector) => {
            return {
              label: sector,
              value: sector,
            };
          })}
        />
        {findUs === FIND_US_OTHER && (
          <TextField id="findUsOther" label="Please specify" {...methods.register('findUsOther')} />
        )}
        <ContactOptions methods={methods} />
        <Button
          type="submit"
          color="primary"
          disabled={!methods.formState.isDirty || !methods.formState.isValid || isMutating}
        >
          Submit
        </Button>
      </Form>
    </FormProvider>
  );
};

const QuoteForm = () => {
  const router = useRouter();
  const businessType = router.query.businessType ?? DEFAULT_BUSINESS_TYPE;

  const onSubmit = useCallback(() => {
    router.push(`/quote-form/thank-you?businessType=${businessType}`);
  }, [businessType, router]);

  return (
    <MainContainer>
      <NextSeo title="Request a bespoke quote" noindex={true} nofollow={true} />
      <FormSection>
        <Container>
          <FormPageTitle>Request a bespoke quote</FormPageTitle>
          <FormParagraph>
            Complete this short form to discuss your energy requirements with a member of our team at a time that suits
            you.
          </FormParagraph>
          <FormParagraph>All fields marked with an (*) are mandatory</FormParagraph>
          <FormInner onSubmit={onSubmit} />
          <FormParagraph>
            <Privacy />
          </FormParagraph>
        </Container>
      </FormSection>
      <SmallMessagesToaster variant="small" />
    </MainContainer>
  );
};

export default QuoteForm;
